import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { LIST_PINNED_MESSAGES } from '../../graphql/operations';
import './PinnedMessagesBanner.css';

/**
 * KAP-311.2 — collapsible banner above the message list listing every pinned
 * message in the conversation, oldest pin first (matches the backend's
 * `listPinnedMessages` ordering).
 *
 * Each row: sender, content excerpt (160-char truncate), Jump (deep-links to
 * the source bubble via the existing `?msg=` route), Unpin. Empty list ⇒
 * the banner does not render at all.
 *
 * The query runs at the active conversation's level so switching conversations
 * naturally re-runs it. The pin/unpin mutations in UnifiedChatPanel use
 * `cache.modify` to flip both the underlying Message AND this query's list
 * in one shot so the banner is in lockstep with the bubble.
 *
 * Cross-user sync: when another user pins a message, the broadcast lands via
 * the existing `onNewMessage` subscription and the UnifiedChatPanel's onData
 * handler triggers a refetch on this query (see UnifiedChatPanel.js).
 */
const EXCERPT_MAX = 160;

function truncate(text) {
  if (typeof text !== 'string' || text.length === 0) return '';
  if (text.length <= EXCERPT_MAX) return text;
  return `${text.slice(0, EXCERPT_MAX - 1)}…`;
}

function PinnedMessagesBanner({ conversationId, onJumpToMessage, onUnpinMessage }) {
  const [collapsed, setCollapsed] = useState(false);
  const { data, loading, error } = useQuery(LIST_PINNED_MESSAGES, {
    variables: { conversationId },
    skip: !conversationId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false
  });

  if (loading && !data) return null;
  if (error) return null;
  const pinned = data?.listPinnedMessages || [];
  if (pinned.length === 0) return null;

  return (
    <div className="pinned-messages-banner" role="region" aria-label="Pinned messages">
      <button
        type="button"
        className="pinned-messages-banner-header"
        aria-expanded={!collapsed}
        onClick={() => setCollapsed((prev) => !prev)}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
          <path d="M16 9V4l1-1V2H7v1l1 1v5L4 14v2h6v6l1 1 1-1v-6h6v-2l-4-5z" />
        </svg>
        <span className="pinned-messages-banner-title">
          {pinned.length} pinned {pinned.length === 1 ? 'message' : 'messages'}
        </span>
        <svg
          viewBox="0 0 24 24"
          width="14"
          height="14"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
          className={`pinned-messages-banner-chevron ${collapsed ? 'collapsed' : ''}`}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {!collapsed && (
        <ul className="pinned-messages-banner-list">
          {pinned.map((msg) => (
            <li key={msg.id} className="pinned-messages-banner-row">
              <div className="pinned-messages-banner-row-text">
                <span className="pinned-messages-banner-sender">
                  {msg.senderDisplayName || msg.senderId || 'Unknown'}
                </span>
                <span className="pinned-messages-banner-excerpt">
                  {truncate(msg.content)}
                </span>
              </div>
              <div className="pinned-messages-banner-row-actions">
                <button
                  type="button"
                  className="pinned-messages-banner-action"
                  onClick={() => onJumpToMessage && onJumpToMessage(msg.id)}
                  aria-label={`Jump to pinned message from ${msg.senderDisplayName || 'Unknown'}`}
                >
                  Jump
                </button>
                <button
                  type="button"
                  className="pinned-messages-banner-action pinned-messages-banner-action-destructive"
                  onClick={() => onUnpinMessage && onUnpinMessage(msg.id, conversationId)}
                  aria-label="Unpin message"
                >
                  Unpin
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default PinnedMessagesBanner;
